import { useEffect, useMemo, useReducer, useRef } from "react";
import Decimal from "decimal.js";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import {
  TtOrderMetadata,
  TtOrderMetadataCustomerSplit,
  TtOrderSnapshot,
  useUpsertOrderMetadataV2,
} from "@/lib/api/risk/brokerage/tt";
import { Commodities } from "@/lib/api/commodity";
import { CommonDestinationTimezone } from "@/lib/utils";
import { getSuggestedCustomerId } from "@/lib/tt/suggestCustomer";
import { getSuggestedOrderType } from "@/lib/tt/suggestOrderType";
import { buildLinkedOrderGrouping } from "@/lib/tt/linkedOrders";
import type { LinkableOrder } from "@/lib/tt/linkedOrders";
import { metadataToOrderType, orderTypeToExpectedFlags } from "@/lib/tt/orderType";
import {
  AttachFormValues,
  CustomerSplitEntry,
  OrderType,
  OtcContractMode,
  REOWN_CONTRACT_TYPES,
} from "./types";

/**
 * Commodities for which the v2 backend route allows over-allocation by up to
 * 2500 UOM (absolute), but disallows under-allocation. Mirrors
 * OVER_ALLOCATION_COMMODITIES on the backend.
 */
const OVER_ALLOCATION_COMMODITIES = new Set<string>([
  Commodities.Corn,
  Commodities.Soybeans,
  Commodities.MinneapolisWheat,
  Commodities.WinterWheat,
]);

const MAX_OVER_ALLOCATION = new Decimal(2500);

export interface AttachOrderFormSuggestions {
  customerId?: string;
  orderType?: OrderType;
}

export interface UseAttachOrderFormOptions {
  guidOrderId: string;
  snapshot: TtOrderSnapshot;
  existingMetadata: TtOrderMetadata | null;
  /**
   * Customer options provided by the parent component. Required for the
   * customer-suggestion engine. Pass [] when the customer list is still
   * loading; suggestions will simply be undefined until it arrives.
   */
  customers: Array<{ id: string; longName: string; hasRiskProfile?: boolean }>;
  /**
   * Already-fetched orders that can be linked to this one. Only consulted on
   * submit, to derive the link's `sub_group_id`; the picker itself ranks them
   * in the view. Pass [] when they are not loaded yet.
   */
  linkedOrderCandidates?: LinkableOrder[];
  onSaved?: () => void;
}

export interface UseAttachOrderFormResult {
  isUpdatable: boolean;
  values: AttachFormValues;
  /** Snapshot of the values computed from existingMetadata at mount, never mutated. */
  originalValues: AttachFormValues;
  dirtyFields: Set<keyof AttachFormValues>;
  suggestions: AttachOrderFormSuggestions;
  /** Pre-computed totals for UI. */
  totals: {
    totalQuantity: Decimal | null;
    assigned: Decimal;
    lhcRemainder: Decimal;
  };
  errors: Partial<Record<keyof AttachFormValues, string>>;
  isValid: boolean;
  isSubmitting: boolean;
  setField: <K extends keyof AttachFormValues>(key: K, value: AttachFormValues[K]) => void;
  /** Show/hide the linked-order picker; hiding it clears every link. */
  changeLinkedOrderEnabled: (enabled: boolean) => void;
  changeLinkedOrder: (index: number, guidOrderId: string) => void;
  addLinkedOrder: () => void;
  removeLinkedOrder: (index: number) => void;
  changeOrderType: (orderType: OrderType) => void;
  changeOtcContractMode: (mode: OtcContractMode) => void;
  changeCustomerSplitQuantity: (index: number, value: string) => void;
  changeCustomerSplitCustomer: (index: number, customerId: string) => void;
  addCustomerSplit: () => void;
  removeCustomerSplit: (index: number) => void;
  applyCustomerSuggestion: () => void;
  applyOrderTypeSuggestion: () => void;
  submit: () => Promise<void>;
}

type AttachFormField = keyof AttachFormValues;

let __keySeq = 0;
function makeKey(): string {
  __keySeq += 1;
  return `s${__keySeq}_${Date.now()}`;
}

function getOtcContractModeFromMetadata(meta: TtOrderMetadata | null): OtcContractMode | null {
  if (meta?.otc_contract_amendment_type) return "amendment";
  const t = meta?.otc_contract_type;
  if (!t) return null;
  return REOWN_CONTRACT_TYPES.has(t) ? "reown" : "hedgeable";
}

/** Convert a split_percentage (0–100) to a quantity given a totalQuantity. */
function percentageToQuantity(pct: number | string, total: Decimal): Decimal {
  return new Decimal(pct).div(100).mul(total).toDecimalPlaces(0);
}

function buildOriginalValues({
  snapshot,
  metadata,
}: {
  snapshot: TtOrderSnapshot;
  metadata: TtOrderMetadata | null;
}): AttachFormValues {
  const total = snapshot.order_uom_quantity != null ? new Decimal(snapshot.order_uom_quantity) : null;
  const currentYear = moment.tz(CommonDestinationTimezone).format("YYYY");

  const splits: CustomerSplitEntry[] = (() => {
    if (metadata?.SpecifiedCustomers && metadata.SpecifiedCustomers.length > 0) {
      return metadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit) => ({
        key: makeKey(),
        customer_id: c.customer_id,
        quantity: total != null
          ? percentageToQuantity(c.split_percentage, total).toFixed(0)
          : String(c.split_percentage),
      }));
    }
    return [{
      key: makeKey(),
      customer_id: "",
      quantity: total != null ? total.toFixed(0) : "",
    }];
  })();

  return {
    customerSplits: splits,
    cropYear: metadata?.crop_year && metadata.crop_year !== 0
      ? String(metadata.crop_year)
      : currentYear,
    orderType: metadataToOrderType(metadata),
    otcContractMode: getOtcContractModeFromMetadata(metadata),
    otcContractType: metadata?.otc_contract_type ?? "",
    amendmentType: metadata?.otc_contract_amendment_type ?? "",
    contractNumber: metadata?.contract_number ?? "",
    basis: metadata?.basis != null ? String(metadata.basis) : "",
    marketZoneId: metadata?.market_zone_id ?? "",
    deliveryTerms: metadata?.delivery_terms ?? "",
    shouldIgnore: metadata?.should_ignore_order ?? false,
    isNonClient: false,
    linkedOrderEnabled: false,
    linkedOrders: [],
  };
}

function sumQuantities(splits: CustomerSplitEntry[]): Decimal {
  return splits.reduce((sum, s) => {
    try {
      return s.quantity ? sum.plus(new Decimal(s.quantity)) : sum;
    } catch {
      return sum;
    }
  }, new Decimal(0));
}

function recalculateFirstRowQuantity({
  splits,
  totalQuantity,
}: {
  splits: CustomerSplitEntry[];
  totalQuantity: Decimal | null;
}): CustomerSplitEntry[] {
  if (totalQuantity == null || splits.length <= 1) {
    return splits;
  }

  const otherSum = splits.slice(1).reduce((sum, entry) => {
    try {
      return entry.quantity ? sum.plus(new Decimal(entry.quantity)) : sum;
    } catch {
      return sum;
    }
  }, new Decimal(0));

  const remainder = Decimal.max(0, totalQuantity.minus(otherSum));
  return [{ ...splits[0], quantity: remainder.toFixed(0) }, ...splits.slice(1)];
}

/**
 * Immutably set a single field. Kept generic so the caller (`setField`) keeps
 * key/value type-checking all the way to the public hook API.
 */
function withField<K extends AttachFormField>(
  values: AttachFormValues,
  key: K,
  value: AttachFormValues[K],
): AttachFormValues {
  return { ...values, [key]: value };
}

function withDirtyFields(
  dirtyFields: Set<AttachFormField>,
  keys: readonly AttachFormField[],
): Set<AttachFormField> {
  const next = new Set(dirtyFields);
  for (const key of keys) {
    next.add(key);
  }
  return next;
}

/**
 * Fields owned by the OTC-contract branch. `otcContractMode` selects the OTC
 * intent (hedgeable / reown / amendment), so all four are cleared together.
 */
const OTC_CONTRACT_BRANCH_FIELDS: readonly AttachFormField[] = [
  "otcContractMode",
  "otcContractType",
  "amendmentType",
  "contractNumber",
];

/** Fields owned by the Cash general-contract branch. */
const GENERAL_CONTRACT_CASH_BRANCH_FIELDS: readonly AttachFormField[] = [
  "basis",
  "marketZoneId",
  "deliveryTerms",
];

function clearOtcContractBranch(values: AttachFormValues): AttachFormValues {
  return { ...values, otcContractMode: null, otcContractType: "", amendmentType: "", contractNumber: "" };
}


function clearGeneralContractCashBranch(values: AttachFormValues): AttachFormValues {
  return { ...values, basis: "", marketZoneId: "", deliveryTerms: "" };
}

export interface AttachOrderFormState {
  values: AttachFormValues;
  dirtyFields: Set<AttachFormField>;
  /** True once the user has typed a quantity into the first split row directly. */
  firstRowUserEdited: boolean;
  isSubmitting: boolean;
}

/**
 * Every mutation of the form goes through exactly one of these actions, so the
 * clearing rules for mutually-exclusive branches live in a single place instead
 * of being spread across the call sites.
 */
export type AttachOrderFormAction =
  /** Set one field verbatim. Use `orderTypeChanged`/`otcContractModeChanged` when the change owns a branch. */
  | { type: "fieldSet"; key: AttachFormField; value: AttachFormValues[AttachFormField] }
  /** Set the order type, clearing every branch that is no longer active. */
  | { type: "orderTypeChanged"; orderType: OrderType }
  /** Show/hide the linked-order picker. Hiding it drops every chosen order. */
  | { type: "linkedOrderEnabledChanged"; enabled: boolean; key: string }
  /** Set the order chosen in one link row. */
  | { type: "linkedOrderChanged"; index: number; guidOrderId: string }
  | { type: "linkedOrderAdded"; key: string }
  | { type: "linkedOrderRemoved"; index: number }
  /** Set the OTC-contract mode, clearing both the contract type and the amendment. */
  | { type: "otcContractModeChanged"; mode: OtcContractMode }
  | { type: "splitQuantityChanged"; index: number; quantity: string; totalQuantity: Decimal | null }
  | { type: "splitCustomerChanged"; index: number; customerId: string; totalQuantity: Decimal | null }
  | { type: "splitAdded"; key: string }
  | { type: "splitRemoved"; index: number; totalQuantity: Decimal | null }
  | { type: "customerSuggestionApplied"; customerId: string; key: string; totalQuantity: Decimal | null }
  /** Discard all edits and rebuild from the provided baseline (order changed / explicit reset). */
  | { type: "reset"; values: AttachFormValues }
  | { type: "submitStarted" }
  | { type: "submitFinished" };

export function createAttachOrderFormState(values: AttachFormValues): AttachOrderFormState {
  return {
    values,
    dirtyFields: new Set<AttachFormField>(),
    firstRowUserEdited: false,
    isSubmitting: false,
  };
}

export function attachOrderFormReducer(
  state: AttachOrderFormState,
  action: AttachOrderFormAction,
): AttachOrderFormState {
  switch (action.type) {
    case "fieldSet":
      return {
        ...state,
        values: withField(state.values, action.key, action.value),
        dirtyFields: withDirtyFields(state.dirtyFields, [action.key]),
      };

    case "orderTypeChanged": {
      let values = withField(state.values, "orderType", action.orderType);
      const touched: AttachFormField[] = ["orderType"];

      // Leaving OTC drops the whole OTC branch.
      if (action.orderType !== "otc") {
        values = clearOtcContractBranch(values);
        touched.push(...OTC_CONTRACT_BRANCH_FIELDS);
      }

      // The general-contract fields belong only to Cash orders.
      if (action.orderType !== "cash") {
        values = clearGeneralContractCashBranch(values);
        touched.push(...GENERAL_CONTRACT_CASH_BRANCH_FIELDS);
      }

      return { ...state, values, dirtyFields: withDirtyFields(state.dirtyFields, touched) };
    }

    case "linkedOrderEnabledChanged":
      // Turning the picker off drops the selections, so a later re-enable
      // cannot submit links the user can no longer see. Turning it on starts
      // from one empty row, mirroring the customer splits.
      return {
        ...state,
        values: {
          ...state.values,
          linkedOrderEnabled: action.enabled,
          linkedOrders: action.enabled
            ? (state.values.linkedOrders.length > 0
                ? state.values.linkedOrders
                : [{ key: action.key, guid_order_id: "" }])
            : [],
        },
        dirtyFields: withDirtyFields(state.dirtyFields, ["linkedOrderEnabled", "linkedOrders"]),
      };

    case "linkedOrderChanged":
      return {
        ...state,
        values: {
          ...state.values,
          linkedOrders: state.values.linkedOrders.map((entry, i) => (
            i === action.index ? { ...entry, guid_order_id: action.guidOrderId } : entry
          )),
        },
        dirtyFields: withDirtyFields(state.dirtyFields, ["linkedOrders"]),
      };

    case "linkedOrderAdded":
      return {
        ...state,
        values: {
          ...state.values,
          linkedOrders: [
            ...state.values.linkedOrders,
            { key: action.key, guid_order_id: "" },
          ],
        },
        dirtyFields: withDirtyFields(state.dirtyFields, ["linkedOrders"]),
      };

    case "linkedOrderRemoved":
      return {
        ...state,
        values: {
          ...state.values,
          linkedOrders: state.values.linkedOrders.filter((_, i) => i !== action.index),
        },
        dirtyFields: withDirtyFields(state.dirtyFields, ["linkedOrders"]),
      };

    case "otcContractModeChanged": {
      // The contract-type option list depends on the mode, and a contract type
      // and an amendment are mutually exclusive, so neither side is carried
      // across a mode change.
      const values: AttachFormValues = {
        ...state.values,
        otcContractMode: action.mode,
        otcContractType: "",
        amendmentType: "",
        contractNumber: "",
      };

      return {
        ...state,
        values,
        dirtyFields: withDirtyFields(state.dirtyFields, OTC_CONTRACT_BRANCH_FIELDS),
      };
    }

    case "splitQuantityChanged": {
      const editedSplits = state.values.customerSplits.map((entry, i) => (
        i === action.index ? { ...entry, quantity: action.quantity } : entry
      ));

      // Editing any row other than the first re-derives the first row from the
      // remaining quantity, until the user takes direct control of row 0.
      const nextSplits = action.index !== 0 && !state.firstRowUserEdited && action.totalQuantity != null
        ? recalculateFirstRowQuantity({ splits: editedSplits, totalQuantity: action.totalQuantity })
        : editedSplits;

      return {
        ...state,
        values: { ...state.values, customerSplits: nextSplits },
        dirtyFields: withDirtyFields(state.dirtyFields, ["customerSplits"]),
        firstRowUserEdited: state.firstRowUserEdited || action.index === 0,
      };
    }

    case "splitCustomerChanged": {
      const assignedSplits = state.values.customerSplits.map((entry, i) => (
        i === action.index ? { ...entry, customer_id: action.customerId } : entry
      ));

      // Choosing a customer for the only row fills in the full order quantity
      // while that quantity is still blank.
      const nextSplits = assignedSplits.length === 1
        && !assignedSplits[0]?.quantity
        && action.totalQuantity != null
        ? [{ ...assignedSplits[0], quantity: action.totalQuantity.toFixed(0) }]
        : assignedSplits;

      return {
        ...state,
        values: { ...state.values, customerSplits: nextSplits },
        dirtyFields: withDirtyFields(state.dirtyFields, ["customerSplits"]),
      };
    }

    case "splitAdded":
      return {
        ...state,
        values: {
          ...state.values,
          customerSplits: [
            ...state.values.customerSplits,
            { key: action.key, customer_id: "", quantity: "" },
          ],
        },
        dirtyFields: withDirtyFields(state.dirtyFields, ["customerSplits"]),
      };

    case "splitRemoved": {
      const remainingSplits = state.values.customerSplits.filter((_, i) => i !== action.index);

      const nextSplits = !state.firstRowUserEdited
        && action.totalQuantity != null
        && remainingSplits.length > 1
        ? recalculateFirstRowQuantity({ splits: remainingSplits, totalQuantity: action.totalQuantity })
        : remainingSplits;

      return {
        ...state,
        values: { ...state.values, customerSplits: nextSplits },
        dirtyFields: withDirtyFields(state.dirtyFields, ["customerSplits"]),
      };
    }

    case "customerSuggestionApplied": {
      // No rows at all: seed a single row with the suggested customer.
      if (state.values.customerSplits.length === 0) {
        return {
          ...state,
          values: {
            ...state.values,
            customerSplits: [{
              key: action.key,
              customer_id: action.customerId,
              quantity: action.totalQuantity != null ? action.totalQuantity.toFixed(0) : "",
            }],
          },
          dirtyFields: withDirtyFields(state.dirtyFields, ["customerSplits"]),
        };
      }

      // Otherwise reuse the row-0 customer assignment rules.
      return attachOrderFormReducer(state, {
        type: "splitCustomerChanged",
        index: 0,
        customerId: action.customerId,
        totalQuantity: action.totalQuantity,
      });
    }

    case "reset":
      // Values, dirty tracking and the first-row flag all go back to the
      // baseline, but an in-flight submit is not ours to cancel.
      return { ...createAttachOrderFormState(action.values), isSubmitting: state.isSubmitting };

    case "submitStarted":
      return { ...state, isSubmitting: true };

    case "submitFinished":
      return { ...state, isSubmitting: false };
  }
}

export function useAttachOrderForm({
  guidOrderId,
  snapshot,
  existingMetadata,
  customers,
  linkedOrderCandidates = [],
  onSaved,
}: UseAttachOrderFormOptions): UseAttachOrderFormResult {
  const { toast } = useToast();
  const upsertMetadata = useUpsertOrderMetadataV2();
  const isUpdatable = !(existingMetadata?.has_necessary_info || existingMetadata?.should_ignore_order);

  // Baseline for the current order. Recomputed only when the order changes
  // (by guid) so in-progress edits are never clobbered by a re-render.
  const originalValues = useMemo(
    () => buildOriginalValues({ snapshot, metadata: existingMetadata }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guidOrderId]
  );

  const [state, dispatch] = useReducer(attachOrderFormReducer, originalValues, createAttachOrderFormState);

  // Consumers normally remount the form via key={guidOrderId}; this keeps the
  // hook correct on its own when the order changes without a remount.
  const lastGuidOrderIdRef = useRef(guidOrderId);
  useEffect(() => {
    if (lastGuidOrderIdRef.current === guidOrderId) return;
    lastGuidOrderIdRef.current = guidOrderId;
    dispatch({ type: "reset", values: originalValues });
  }, [guidOrderId, originalValues]);

  const suggestionCustomers = useMemo(
    () => customers.filter((customer) => {
      if (state.values.isNonClient) return !customer.hasRiskProfile;
      return !!customer.hasRiskProfile;
    }),
    [customers, state.values.isNonClient]
  );

  const suggestions = useMemo<AttachOrderFormSuggestions>(() => {
    if (existingMetadata) {
      // Don't suggest over existing data; show only fresh suggestions for unset fields.
      // (Behavior preserved from v1.)
    }
    const customerId = getSuggestedCustomerId({
      orderTextFields: {
        textA: snapshot.text_a,
        textB: snapshot.text_b,
        textC: snapshot.text_c,
        textTT: snapshot.text_tt,
      },
      customers: suggestionCustomers,
    });
    const orderType = getSuggestedOrderType({
      textA: snapshot.text_a,
      textB: snapshot.text_b,
      textC: snapshot.text_c,
      textTT: snapshot.text_tt,
    });
    return {
      customerId,
      orderType: orderType ?? undefined,
    };
  }, [snapshot, suggestionCustomers, existingMetadata]);

  const totalQuantity = useMemo<Decimal | null>(
    () => snapshot.order_uom_quantity != null ? new Decimal(snapshot.order_uom_quantity) : null,
    [snapshot.order_uom_quantity]
  );

  const assigned = useMemo(
    () => sumQuantities(state.values.customerSplits),
    [state.values.customerSplits]
  );
  const lhcRemainder = useMemo(
    () => totalQuantity != null ? Decimal.max(0, totalQuantity.minus(assigned)) : new Decimal(0),
    [totalQuantity, assigned]
  );

  const errors = useMemo<Partial<Record<keyof AttachFormValues, string>>>(() => {
    const out: Partial<Record<keyof AttachFormValues, string>> = {};
    const values = state.values;

    const firstCustomerId = values.customerSplits[0]?.customer_id ?? "";
    const hasNamedCustomer = values.customerSplits.some((s: CustomerSplitEntry) => s.customer_id && s.quantity);
    const hasLhc = lhcRemainder.gt(0);

    const commodityId = snapshot.commodity_id;
    const isCanola = commodityId === Commodities.Canola;
    const isOverAllocation = commodityId != null && OVER_ALLOCATION_COMMODITIES.has(commodityId);

    if (!firstCustomerId) {
      out.customerSplits = "Please select at least one customer.";
    } else if (!hasNamedCustomer && !hasLhc) {
      out.customerSplits = "Select at least one customer or leave a remainder for LHC.";
    } else if (isCanola) {
      // No quantity bounds for canola.
    } else if (isOverAllocation && totalQuantity != null) {
      const maxAllowed = totalQuantity.plus(MAX_OVER_ALLOCATION);
      if (assigned.gt(maxAllowed)) {
        out.customerSplits = `Assigned quantity (${assigned.toFixed(0)}) must not exceed order quantity (${totalQuantity.toFixed(0)}) by more than 2500.`;
      }
      // Note: assigned >= total is naturally satisfied because
      // lhcRemainder = max(0, total - assigned) is always added back via
      // the LHC row at submit time. The "customers > total => lhc must be 0"
      // backend rule is also auto-satisfied: when customers_sum > total,
      // lhcRemainder is 0 by definition (max with 0).
    } else if (totalQuantity != null && assigned.gt(totalQuantity)) {
      out.customerSplits = `Customer quantities (${assigned.toFixed(0)}) exceed total (${totalQuantity.toFixed(0)}).`;
    }

    if (!values.cropYear) {
      out.cropYear = "Crop year is required.";
    }

    if (values.linkedOrderEnabled && !values.linkedOrders.some((entry) => entry.guid_order_id)) {
      out.linkedOrders = "Select at least one order to link.";
    }

    if (values.orderType === "none") {
      out.orderType = "Order type is required.";
    }

    if (values.orderType === "otc") {
      if (values.otcContractMode === "amendment") {
        if (!values.amendmentType) {
          out.amendmentType = "Amendment type is required.";
        }
      } else if (!values.otcContractType) {
        out.otcContractType = "OTC contract type is required.";
      }
    }

    if (values.orderType === "cash") {
      if (!values.basis) {
        out.basis = "Basis is required.";
      }
      if (!values.marketZoneId) {
        out.marketZoneId = "Market zone is required.";
      }
      if (!values.deliveryTerms) {
        out.deliveryTerms = "Delivery terms are required.";
      }
    }

    return out;
  }, [state.values, totalQuantity, assigned, lhcRemainder, snapshot.commodity_id]);

  const isValid = Object.keys(errors).length === 0;

  function setField<K extends AttachFormField>(key: K, value: AttachFormValues[K]) {
    dispatch({ type: "fieldSet", key, value });
  }

  /** Show/hide the linked-order picker. */
  function changeLinkedOrderEnabled(enabled: boolean) {
    dispatch({ type: "linkedOrderEnabledChanged", enabled, key: makeKey() });
  }

  function changeLinkedOrder(index: number, guidOrderId: string) {
    dispatch({ type: "linkedOrderChanged", index, guidOrderId });
  }

  function addLinkedOrder() {
    dispatch({ type: "linkedOrderAdded", key: makeKey() });
  }

  function removeLinkedOrder(index: number) {
    dispatch({ type: "linkedOrderRemoved", index });
  }

  /** Change the order type, clearing whichever branch is no longer active. */
  function changeOrderType(orderType: OrderType) {
    dispatch({ type: "orderTypeChanged", orderType });
  }

  /** Change the OTC mode, clearing the contract type and the amendment. */
  function changeOtcContractMode(mode: OtcContractMode) {
    dispatch({ type: "otcContractModeChanged", mode });
  }

  function changeCustomerSplitQuantity(index: number, quantity: string) {
    dispatch({ type: "splitQuantityChanged", index, quantity, totalQuantity });
  }

  function changeCustomerSplitCustomer(index: number, customerId: string) {
    dispatch({ type: "splitCustomerChanged", index, customerId, totalQuantity });
  }

  function addCustomerSplit() {
    dispatch({ type: "splitAdded", key: makeKey() });
  }

  function removeCustomerSplit(index: number) {
    dispatch({ type: "splitRemoved", index, totalQuantity });
  }

  function applyCustomerSuggestion() {
    if (!suggestions.customerId) return;

    dispatch({
      type: "customerSuggestionApplied",
      customerId: suggestions.customerId,
      key: makeKey(),
      totalQuantity,
    });
  }

  function applyOrderTypeSuggestion() {
    if (!suggestions.orderType) return;

    // Goes through the same action as picking the order type by hand, so the
    // branch-clearing rules still apply.
    changeOrderType(suggestions.orderType);
  }

  async function submit() {
    if (!isUpdatable) return;
    if (state.isSubmitting) return;

    const values = state.values;

    const firstCustomerId = values.customerSplits[0]?.customer_id ?? "";
    if (!firstCustomerId) {
      toast({
        title: "Validation Error",
        description: "Please select at least one customer",
        variant: "destructive",
      });
      return;
    }

    if (!values.cropYear) {
      toast({
        title: "Validation Error",
        description: "Please select a crop year",
        variant: "destructive",
      });
      return;
    }

    if (values.orderType === "none") {
      toast({
        title: "Validation Error",
        description: "Please select a contract type",
        variant: "destructive",
      });
      return;
    }

    if (values.orderType === "otc") {
      if (values.otcContractMode === "amendment" && !values.amendmentType) {
        toast({
          title: "Validation Error",
          description: "Please select an amendment type",
          variant: "destructive",
        });
        return;
      }
      if (values.otcContractMode !== "amendment" && !values.otcContractType) {
        toast({
          title: "Validation Error",
          description: "Please select an OTC contract type",
          variant: "destructive",
        });
        return;
      }
    }

    if (values.orderType === "cash") {
      if (!values.basis) {
        toast({
          title: "Validation Error",
          description: "Please enter a basis",
          variant: "destructive",
        });
        return;
      }
      if (!values.marketZoneId) {
        toast({
          title: "Validation Error",
          description: "Please select a market zone",
          variant: "destructive",
        });
        return;
      }
      if (!values.deliveryTerms) {
        toast({
          title: "Validation Error",
          description: "Please select delivery terms",
          variant: "destructive",
        });
        return;
      }
    }

    if (values.linkedOrderEnabled && !values.linkedOrders.some((entry) => entry.guid_order_id)) {
      toast({
        title: "Validation Error",
        description: "Select at least one order to link",
        variant: "destructive",
      });
      return;
    }

    if (values.linkedOrderEnabled && values.linkedOrders.length !== [...new Set(values.linkedOrders.map((entry) => entry.guid_order_id))].length) {
      toast({
        title: "Validation Error",
        description: "Select at least one order to link",
        variant: "destructive",
      });
      return;
    }

    const validSplits = values.customerSplits.filter((s) => s.customer_id);
    if (validSplits.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one customer",
        variant: "destructive",
      });
      return;
    }

    // Bounds check (per commodity) — server enforces too. Mirrors `errors` above.
    const commodityId = snapshot.commodity_id;
    const isCanola = commodityId === Commodities.Canola;
    const isOverAllocation = commodityId != null && OVER_ALLOCATION_COMMODITIES.has(commodityId);

    if (!isCanola && isOverAllocation && totalQuantity != null) {
      const maxAllowed = totalQuantity.plus(MAX_OVER_ALLOCATION);
      if (assigned.gt(maxAllowed)) {
        toast({
          title: "Validation Error",
          description: `Assigned quantity (${assigned.toFixed(0)}) must not exceed order quantity (${totalQuantity.toFixed(0)}) by more than 2500`,
          variant: "destructive",
        });
        return;
      }
    } else if (!isCanola && totalQuantity != null && assigned.gt(totalQuantity)) {
      toast({
        title: "Customer quantities",
        description: `(${assigned.toFixed(0)}) exceeds total order quantity (${totalQuantity.toFixed(0)})`,
        variant: "destructive",
      });
      return;
    }

    const customersUom = validSplits.map((s: CustomerSplitEntry) => ({
      customer_id: s.customer_id,
      uom_quantity: s.quantity || "0",
    }));

    const lhcUomQuantity: string | null = lhcRemainder.gt(0) ? lhcRemainder.toString() : null;

    const linkedOrderGrouping = buildLinkedOrderGrouping({
      enabled: values.linkedOrderEnabled,
      linkedGuidOrderIds: values.linkedOrders.map((entry) => entry.guid_order_id),
      candidates: linkedOrderCandidates,
      currentInstrumentName: snapshot.instrument_name,
    });

    dispatch({ type: "submitStarted" });
    try {
      const isOtc = values.orderType === "otc";
      const isAmendment = isOtc && values.otcContractMode === "amendment";
      const usesGeneralContractCashFields = values.orderType === "cash";

      const result = await upsertMetadata.mutateAsync({
        guid_order_id: guidOrderId,
        ...linkedOrderGrouping,
        ...orderTypeToExpectedFlags(values.orderType),
        // Contract type and amendment are mutually exclusive, so the inactive
        // side is sent as null to clear any previously stored value.
        otc_contract_type: isOtc && !isAmendment ? values.otcContractType : null,
        otc_contract_amendment_type: isAmendment ? values.amendmentType : null,
        contract_number: isAmendment ? (values.contractNumber || null) : null,
        market_zone_id: usesGeneralContractCashFields ? (values.marketZoneId || null) : null,
        basis: usesGeneralContractCashFields ? (values.basis || null) : null,
        delivery_terms: usesGeneralContractCashFields ? (values.deliveryTerms || null) : null,
        should_ignore_order: values.shouldIgnore,
        crop_year: parseInt(values.cropYear),
        lhc_uom_quantity: lhcUomQuantity,
        customers: customersUom,
      });

      if (result.success) {
        toast({ title: "Saved", description: "Order metadata updated successfully." });
        onSaved?.();
      } else {
        throw new Error(result.message || "Failed to update metadata");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update metadata";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      dispatch({ type: "submitFinished" });
    }
  }

  return {
    isUpdatable,
    values: state.values,
    originalValues,
    dirtyFields: state.dirtyFields,
    suggestions,
    totals: { totalQuantity, assigned, lhcRemainder },
    errors,
    isValid,
    isSubmitting: state.isSubmitting,
    setField,
    changeLinkedOrderEnabled,
    changeLinkedOrder,
    addLinkedOrder,
    removeLinkedOrder,
    changeOrderType,
    changeOtcContractMode,
    changeCustomerSplitQuantity,
    changeCustomerSplitCustomer,
    addCustomerSplit,
    removeCustomerSplit,
    applyCustomerSuggestion,
    applyOrderTypeSuggestion,
    submit,
  };
}
